// Providers.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSpinner, FaSync, FaCopy } from "react-icons/fa";

function Balance() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingBalances, setFetchingBalances] = useState(false);
    const navigate = useNavigate();

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 1000,
        totalRecords: 0,
        totalPages: 1
    });

    // Log modal states
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [copied, setCopied] = useState(false);

    // Fetch Providers with pagination
    const fetchProviders = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page: page,
                limit: pagination.perPage,
                category: '1'
            };

            const result = await apiCall(
                "providers",
                params,
                true
            );

            if (result.status === true) {
                setProviders(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
                // After fetching providers, fetch balances for all
                if (result.data && result.data.length > 0) {
                    await fetchAllBalances(result.data);
                }
            } else {
                toast.error(result.message || "Failed to fetch providers");
                setProviders([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setProviders([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch balance for a single provider
    const fetchProviderBalance = async (providerId) => {
        try {
            const result = await apiCall(
                "provider-balance",
                { provider_id: providerId },
                true
            );

            if (result.status === true && result.data) {
                return {
                    balance: result.data.balance || '0.00',
                    request: result.data.request || '',
                    response: result.data.response || ''
                };
            } else {
                return {
                    balance: '0.00',
                    request: '',
                    response: result.message || 'Failed to fetch balance'
                };
            }
        } catch (error) {
            return {
                balance: '0.00',
                request: '',
                response: 'Network error'
            };
        }
    };

    // Format JSON for display
    const formatJSON = (jsonString) => {
        try {
            // Try to parse and format as JSON
            const parsed = JSON.parse(jsonString);
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            // If not valid JSON, return as is
            return jsonString;
        }
    };

    // Fetch balances for all providers
    const fetchAllBalances = async (providersList) => {
        setFetchingBalances(true);

        try {
            // Update each provider's balance one by one
            const updatedProviders = [...providersList];

            for (let i = 0; i < updatedProviders.length; i++) {
                const provider = updatedProviders[i];
                const balanceData = await fetchProviderBalance(provider.id);

                // Update provider with balance and log data
                updatedProviders[i] = {
                    ...provider,
                    balance: balanceData.balance,
                    balance_request: balanceData.request,
                    balance_response: balanceData.response
                };
            }

            setProviders(updatedProviders);
        } catch (error) {
            console.error('Error fetching balances:', error);
            toast.error('Failed to fetch some provider balances');
        } finally {
            setFetchingBalances(false);
        }
    };

    // Refresh balances
    const handleRefreshBalances = async () => {
        if (providers.length > 0) {
            await fetchAllBalances(providers);
            toast.success('Balances refreshed successfully!');
        }
    };

    // Show log modal
    const handleShowLogs = (provider) => {
        setSelectedLog({
            provider_name: provider.name,
            request: provider.balance_request || 'No request data',
            response: provider.balance_response || 'No response data',
            balance: provider.balance || '0.00'
        });
        setLogModalOpen(true);
        setCopied(false);
    };

    // Copy to clipboard
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Initial fetch
    useEffect(() => {
        fetchProviders(1);
    }, []);

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchProviders(newPage);
        }
    };

    // Get balance color based on value
    const getBalanceColor = (balance) => {
        const num = parseFloat(balance || 0);
        if (num > 100) return 'text-green-600';
        if (num > 50) return 'text-yellow-600';
        if (num > 10) return 'text-orange-600';
        return 'text-red-600';
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Providers</h1>
                            <p className="text-gray-600 text-sm">View all providers and their balances</p>
                        </div>
                        <button
                            onClick={handleRefreshBalances}
                            disabled={fetchingBalances || loading}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            {fetchingBalances ? (
                                <>
                                    <FaSpinner className="animate-spin" />
                                    Fetching Balances...
                                </>
                            ) : (
                                <>
                                    <FaSync />
                                    Refresh Balances
                                </>
                            )}
                        </button>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{providers.length}</span> providers
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {(loading || fetchingBalances) && (
                            <div className="flex items-center text-sm text-gray-500">
                                <FaSpinner className="animate-spin h-4 w-4 mr-2 text-blue-600" />
                                {loading ? 'Loading providers...' : 'Fetching balances...'}
                            </div>
                        )}
                    </div>

                    {/* Providers Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Provider Name
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Balance
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {providers.length > 0 ? (
                                        providers.map((provider) => (
                                            <tr key={provider.id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                                                    #{provider.id}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {provider.name}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`text-sm font-semibold ${getBalanceColor(provider.balance)}`}>
                                                        ₹{parseFloat(provider.balance || 0).toFixed(2)}
                                                    </span>
                                                    {fetchingBalances && (
                                                        <FaSpinner className="inline-block ml-2 animate-spin h-3 w-3 text-gray-400" />
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {provider.date}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleShowLogs(provider)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="View Logs"
                                                    >
                                                        <FaEye className="text-sm" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No providers found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try refreshing the page</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Log Modal - Light theme with Dark Content Area */}
            {logModalOpen && selectedLog && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setLogModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            {/* Header - Light */}
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">API Logs</h3>
                                    <p className="text-sm text-gray-500">Provider: {selectedLog.provider_name}</p>
                                </div>
                                <button
                                    onClick={() => setLogModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content - Light background with Dark log areas */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
                                {/* Balance - Light */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Balance</h4>
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                        <span className="text-lg font-bold text-green-600">
                                            ₹{parseFloat(selectedLog.balance || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>

                                {/* Request - Dark Content */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-sm font-medium text-gray-700">Request</h4>
                                        <button
                                            onClick={() => handleCopy(selectedLog.request)}
                                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                        >
                                            <FaCopy className="h-3 w-3" />
                                            {copied ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 overflow-x-auto">
                                        <pre className="text-xs text-gray-300 whitespace-pre-wrap break-all font-mono">
                                            {formatJSON(selectedLog.request)}
                                        </pre>
                                    </div>
                                </div>

                                {/* Response - Dark Content */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-sm font-medium text-gray-700">Response</h4>
                                        <button
                                            onClick={() => handleCopy(selectedLog.response)}
                                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                        >
                                            <FaCopy className="h-3 w-3" />
                                            {copied ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 overflow-x-auto">
                                        <pre className="text-xs text-green-300 whitespace-pre-wrap break-all font-mono">
                                            {formatJSON(selectedLog.response)}
                                        </pre>
                                    </div>
                                </div>
                            </div>

                            {/* Footer - Light */}
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end">
                                <button
                                    onClick={() => setLogModalOpen(false)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default Balance;